import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { formatarContagem } from '@/lib/format';
import { cn } from '@/lib/utils';

export interface PaginacaoBarra {
  /** 1-based. */
  pagina: number;
  tamanho: number;
  /** Registros do filtro (todas as paginas). */
  total: number;
  onPagina: (pagina: number) => void;
  /** Sem isso o tamanho nao e editavel. */
  onTamanho?: (tamanho: number) => void;
  tamanhos?: number[];
}

export interface BarraDeAcoesListaProps {
  /** Botao principal "Novo <coisa>" (o mesmo texto que a tela tinha no topo). */
  novo?: { rotulo: string; onClick: () => void; desabilitado?: boolean; titulo?: string };
  /** Quantas linhas estao selecionadas; as acoes em lote aparecem quando > 0. */
  selecionados?: number;
  lote?: ReactNode;
  paginacao?: PaginacaoBarra;
  /** Total de registros quando a lista nao e paginada ("N registros"). */
  totalSemPaginar?: number;
  /** O botao "Relatorios" da tela. */
  relatorios?: ReactNode;
  className?: string;
}

const BOTAO = 'inline-flex h-9 items-center justify-center gap-1.5 rounded-[10px] px-3 text-small font-medium disabled:opacity-50';

/**
 * Barra flutuante das TELAS DE LISTA (S27): fixa no rodape da area de conteudo, com o botao principal "Novo <coisa>", as acoes em lote
 * (quando ha selecao), a paginacao e o botao Relatorios. Mesma posicao e mesmo reservado de fim de pagina da barra de registro
 * (`--barra-registro-h`), para nunca cobrir a ultima linha. As telas de edicao seguem com a RecordToolbar.
 */
export function BarraDeAcoesLista({ novo, selecionados = 0, lote, paginacao, totalSemPaginar, relatorios, className }: BarraDeAcoesListaProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Reserva o fim da pagina (padding do conteudo) com a altura medida da barra, como a barra de registro.
  useEffect(() => {
    const n = Number(document.body.dataset.barraRegistro ?? '0') + 1;
    document.body.dataset.barraRegistro = String(n);
    const el = ref.current;
    const aplicar = () => {
      if (el) document.body.style.setProperty('--barra-registro-h', `${el.getBoundingClientRect().height + 16}px`);
    };
    aplicar();
    const observador = typeof ResizeObserver === 'undefined' || !el ? null : new ResizeObserver(aplicar);
    if (observador && el) observador.observe(el);
    return () => {
      observador?.disconnect();
      const resto = Number(document.body.dataset.barraRegistro ?? '1') - 1;
      if (resto <= 0) {
        delete document.body.dataset.barraRegistro;
        document.body.style.removeProperty('--barra-registro-h');
      } else document.body.dataset.barraRegistro = String(resto);
    };
  }, []);

  const p = paginacao;
  const paginas = p ? Math.max(1, Math.ceil(p.total / p.tamanho)) : 1;

  const barra = (
    <div
      ref={ref}
      data-testid="barra-acoes-lista"
      className={cn('pointer-events-none fixed bottom-4 left-0 right-[var(--copiloto-w,0px)] z-40 flex justify-center px-3 md:left-[var(--menu-w,17.5rem)]', className)}
    >
      <div role="toolbar" aria-label="Ações da lista" className="pointer-events-auto inline-flex w-fit max-w-full flex-wrap items-center justify-center gap-2 rounded-3xl border border-border bg-white/90 p-1.5 text-[#27272a] shadow-float">
        {novo && (
          <button type="button" onClick={novo.onClick} disabled={novo.desabilitado} title={novo.titulo} className={cn(BOTAO, 'bg-primary px-4 text-primary-foreground hover:opacity-90')}>
            <Plus aria-hidden="true" className="size-[15px]" />
            {novo.rotulo}
          </button>
        )}
        {selecionados > 0 && lote && (
          <div role="group" aria-label={`Ações em lote (${selecionados} selecionada${selecionados > 1 ? 's' : ''})`} className="flex items-center gap-1.5 border-l border-border pl-2">
            <span className="px-1 text-caption text-ink-secondary">{selecionados} selecionada{selecionados > 1 ? 's' : ''}</span>
            {lote}
          </div>
        )}
        {p && (
          <div role="group" aria-label="Paginação" className="flex items-center gap-1 border-l border-border pl-2 text-caption text-ink-secondary">
            <button type="button" aria-label="Página anterior" disabled={p.pagina <= 1} onClick={() => p.onPagina(p.pagina - 1)} className={cn(BOTAO, 'w-9 px-0 hover:bg-line-soft')}>
              <ChevronLeft aria-hidden="true" className="size-4" />
            </button>
            <span data-testid="barra-pagina" className="px-1 tabular-nums">
              Página {formatarContagem(p.pagina)} de {formatarContagem(paginas)} · {formatarContagem(p.total)} registro{p.total === 1 ? '' : 's'}
            </span>
            <button type="button" aria-label="Próxima página" disabled={p.pagina >= paginas} onClick={() => p.onPagina(p.pagina + 1)} className={cn(BOTAO, 'w-9 px-0 hover:bg-line-soft')}>
              <ChevronRight aria-hidden="true" className="size-4" />
            </button>
            {p.onTamanho && (
              <label className="flex items-center gap-1">
                <span className="sr-only">Registros por página</span>
                <select aria-label="Registros por página" value={p.tamanho} onChange={(e) => p.onTamanho?.(Number(e.target.value))} className="h-8 rounded-md border border-input bg-card px-1.5 text-caption">
                  {(p.tamanhos ?? [25, 50, 100, 200]).map((t) => (
                    <option key={t} value={t}>
                      {t} por página
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}
        {!p && totalSemPaginar !== undefined && (
          <span data-testid="barra-total" className="border-l border-border pl-2 pr-1 text-caption text-ink-secondary tabular-nums">
            {formatarContagem(totalSemPaginar)} registro{totalSemPaginar === 1 ? '' : 's'}
          </span>
        )}
        {relatorios && <div className="border-l border-border pl-2">{relatorios}</div>}
      </div>
    </div>
  );
  return createPortal(barra, document.body);
}
